import { Component, computed, inject, model, output, signal } from '@angular/core';
import { IDAuthSession } from '@api/model/iDAuthSession';
import { AuthApiService } from '@api/service/auth.api.service';
import { UserService } from '@core/client/user.service';
import { FeatureFlagsService } from '@core/feature-flags/page-feature-flags.service';
import { ActionClickedEvent } from '@shared/components/list/ag-grid/cell-renderers/actions-cell-renderer.component';
import { ModalComponent } from "@shared/components/modals/modal/modal.component";
import { VoltaSweetAlertService } from '@shared/services/volta-sweet-alert/volta-sweet-alert.service';
import { firstValueFrom, map, Subject } from 'rxjs';
import { SessionListComponent } from "../session-list/session-list.component";
import { initLoginSessionModalFeatureFlags, LoginSessionModalFeatureFlags } from './login-sessions-modal.features';

@Component({
  selector: 'app-login-sessions-modal',
  imports: [ModalComponent, SessionListComponent],
  templateUrl: './login-sessions-modal.component.html',
  styleUrl: './login-sessions-modal.component.scss',
})
export class LoginSessionsModalComponent {
    
    private featureFlagsService = inject(FeatureFlagsService);
    private swalService = inject(VoltaSweetAlertService);
    private userService = inject(UserService);
    private authApiService = inject(AuthApiService);

    visible = model<boolean>(false);

    sessionsBelowLimit = output<boolean>({alias: 'sessions-below-limit'});

    features = signal<LoginSessionModalFeatureFlags>(initLoginSessionModalFeatureFlags());
    
    userSessions = signal<IDAuthSession[]|undefined>(undefined);
    userMaxSessions = computed(() => this.userService.userManageInfo()?.maximumAllowedOfActiveSessions ?? 0);

    refresh$ = new Subject<boolean|'new'|'list'>();

    constructor(){
        this.featureFlagsService.featureFlags$.subscribe(() => {
            this.features.set(this.featureFlagsService.getFeatureFlags("LoginSessionModal"))
        })
    }

    getData(){
        return Promise.all([
            firstValueFrom(this.getOtherUserSessions()),
            firstValueFrom(this.userService.getSessionUserManageInfo()),
        ])
        .then(([sessions]) => {
            if(sessions){
                this.userSessions.set(sessions);
            }
            else{
                this.swalService.fireError(new Error());
            }
        })
        .catch((error) => {
            this.swalService.fireError(error);
        })
    }

    getOtherUserSessions(){
        return this.authApiService.Auth.Manage.Sessions.Get()
        .pipe(
            map((response) => 
                response.filter((session) => session.detail.isActive && !session.isCurrent)
            )
        )
    }
    
    show(){
        //this.refresh$.next('list');
        //TODO: replace with above comment instead (keep visible set to true)
        this.getData()
        .then(() => {
            this.visible.set(true);
        })

    }
    
    hide(){
        this.visible.set(false);
    }

    onActionClicked(event:ActionClickedEvent<IDAuthSession>){
        switch(event.actionId){
            case 'delete':
                this.logoutSession(event);
                break;
        }
    }

    logoutSession(event:ActionClickedEvent<IDAuthSession>){

        this.swalService.fireLoading({message: "Ending session, please wait."});

        let sessionId = event.data?.detail.identifier;

        if(sessionId){
            //Logout Session
            firstValueFrom(this.authApiService.Auth.Logout.Post({
                params:{
                    sessionId: sessionId
                }
            }))
            .then(() => {

                this.swalService.close();

                this.userSessions.update((userSessions) => [
                    ...(userSessions?.filter((session) => session.detail.identifier != sessionId) ?? [])
                ])

                let remainingSessionsLength = Object.entries(this.userSessions() ?? {}).length;

                if(this.userMaxSessions() != 0 && this.userMaxSessions() > remainingSessionsLength){
                    this.hide();
                    this.sessionsBelowLimit.emit(true);
                }
            })
            .catch((error) => {
                this.swalService.fireError(error);
            })
        }
        else{
            this.swalService.fireError(new Error());
        }

    }

}
